import React, { ComponentType, ErrorInfo } from 'react';
import { AgentMessageErrorBoundary } from './AgentMessageErrorBoundary';
import { type ErrorContext } from '@/utils/agentMessageValidator';

/**
 * Options for configuring the error boundary HOC
 */
interface WithErrorBoundaryOptions {
    /**
     * Custom fallback component to render when an error occurs
     */
    fallback?: React.ReactNode;
    
    /**
     * Whether to show retry functionality
     */
    showRetry?: boolean;
    
    /**
     * Custom error handler function
     */
    onError?: (error: Error, errorInfo: ErrorInfo, context: ErrorContext) => void;
    
    /**
     * Component name for logging purposes
     */
    componentName?: string;
    
    /**
     * Whether to isolate errors to prevent propagation
     */
    isolateErrors?: boolean;
}

/**
 * Higher-order component that wraps a component with AgentMessageErrorBoundary
 * 
 * @param WrappedComponent - The component to wrap with error boundary
 * @param options - Configuration options for the error boundary
 * @returns Enhanced component with error boundary protection
 */
export function withAgentMessageErrorBoundary<P extends object>(
    WrappedComponent: ComponentType<P>,
    options: WithErrorBoundaryOptions = {}
): ComponentType<P> {
    const {
        fallback,
        showRetry = true,
        onError,
        componentName,
        isolateErrors = true
    } = options;

    const displayName = componentName || 
                       WrappedComponent.displayName || 
                       WrappedComponent.name || 
                       'Component';

    const WithErrorBoundaryComponent: ComponentType<P> = (props: P) => {
        // If error isolation is disabled, render component directly
        if (!isolateErrors) {
            return <WrappedComponent {...props} />;
        }

        return (
            <AgentMessageErrorBoundary
                fallback={fallback}
                showRetry={showRetry}
                onError={onError}
                componentName={displayName}
            >
                <WrappedComponent {...props} />
            </AgentMessageErrorBoundary>
        );
    };

    WithErrorBoundaryComponent.displayName = `withAgentMessageErrorBoundary(${displayName})`;

    return WithErrorBoundaryComponent;
}

/**
 * Decorator version of the HOC for use with class components
 * 
 * @param options - Configuration options for the error boundary
 * @returns Class decorator function
 */
export function AgentMessageErrorBoundaryDecorator(options: WithErrorBoundaryOptions = {}) {
    return function <T extends ComponentType<any>>(target: T): T {
        return withAgentMessageErrorBoundary(target, options) as T;
    };
}

/**
 * React hook for handling errors within functional components
 * Provides error reporting functionality that integrates with the error boundary system
 */
export function useAgentMessageErrorHandler() {
    const [error, setError] = React.useState<Error | null>(null);

    /**
     * Report an error to the error boundary system
     */
    const reportError = React.useCallback((error: Error, context?: Partial<ErrorContext>) => {
        console.error('[useAgentMessageErrorHandler] Error reported:', error);
        
        // Log error with context
        const errorContext = {
            component: 'useAgentMessageErrorHandler',
            function: 'reportError',
            timestamp: Date.now(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
            url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
            ...context
        };

        console.error('Error Context:', errorContext);
        
        // Set error state to trigger error boundary if this component is wrapped
        setError(error);
    }, []);

    /**
     * Clear the current error state
     */
    const clearError = React.useCallback(() => {
        setError(null);
    }, []);

    /**
     * Safe async function wrapper that catches and reports errors
     */
    const safeAsync = React.useCallback(
        <T extends any[], R>(
            asyncFn: (...args: T) => Promise<R>,
            errorContext?: Partial<ErrorContext>
        ) => {
            return async (...args: T): Promise<R | null> => {
                try {
                    return await asyncFn(...args);
                } catch (error) {
                    reportError(
                        error instanceof Error ? error : new Error(String(error)),
                        errorContext
                    );
                    return null;
                }
            };
        },
        [reportError]
    );

    /**
     * Safe synchronous function wrapper that catches and reports errors
     */
    const safeSync = React.useCallback(
        <T extends any[], R>(
            syncFn: (...args: T) => R,
            errorContext?: Partial<ErrorContext>
        ) => {
            return (...args: T): R | null => {
                try {
                    return syncFn(...args);
                } catch (error) {
                    reportError(
                        error instanceof Error ? error : new Error(String(error)),
                        errorContext
                    );
                    return null;
                }
            };
        },
        [reportError]
    );

    // Throw error to trigger error boundary if one exists
    React.useEffect(() => {
        if (error) {
            throw error;
        }
    }, [error]);

    return {
        reportError,
        clearError,
        safeAsync,
        safeSync,
        hasError: error !== null,
        error
    };
}

/**
 * Predefined error boundary configurations for common use cases
 */
export const ErrorBoundaryPresets = {
    /**
     * Configuration for agent message components
     */
    agentMessage: {
        showRetry: true,
        componentName: 'Agent Message',
        isolateErrors: true
    } as WithErrorBoundaryOptions,

    /**
     * Configuration for streaming message components
     */
    streamingMessage: {
        showRetry: true,
        componentName: 'Streaming Message',
        isolateErrors: true
    } as WithErrorBoundaryOptions,

    /**
     * Configuration for agent icon components
     */
    agentIcon: {
        showRetry: false,
        componentName: 'Agent Icon',
        isolateErrors: true
    } as WithErrorBoundaryOptions,

    /**
     * Configuration for WebSocket message components
     */
    websocketMessage: {
        showRetry: true,
        componentName: 'WebSocket Message',
        isolateErrors: true
    } as WithErrorBoundaryOptions,

    /**
     * Configuration for critical components that should not retry
     */
    critical: {
        showRetry: false,
        componentName: 'Critical Component',
        isolateErrors: false
    } as WithErrorBoundaryOptions
};

export default withAgentMessageErrorBoundary;